import { artMidnightUtcIso, detectAnomalies, jornadasAbiertasEnPeriodo, splitMinutesByDate } from './timesheet-policy.js';
import { esMesCompleto } from './liquidacion-periodo-policy.js';
import { fijoYaLiquidadoEnMes } from './payroll-policy.js';

/**
 * Pure roster/worked-time policy for the Liquidación period dashboard (Unit
 * S2a/S2b, design.md AD3/AD4/AD5/AD19, spec `payroll-period-dashboard`). No
 * DOM, no I/O, no `Date.now()`/`new Date()` internally — every function here
 * is plain data in, plain data out, callable from a test with no mocks. The
 * wiring layer (`js/admin.js`) owns every read (`loadEmpleados`,
 * `loadFichadas`, `previewLiquidacion`, the historial cache) and every
 * "now" instant; this module only classifies and formats what it is handed.
 *
 * S2b moved `splitTableroRoster`/`applyPreviewToFilas`/
 * `computeTableroTotales` here from `js/admin.js` (folded
 * review-5a2ac077e1b2c3d4 follow-up — those functions were originally
 * written inline in `js/admin.js` by S2a). S2b also adds the worked-time
 * functions (`minutosTrabajadosEnRango`/`diasTrabajadosEnRango`/
 * `formatTrabajado`/`trabajadoDeDetalle`) and the "Sin horas" bucket (spec
 * "Row Status Presentation" + "Worked Time Column").
 */

/**
 * Vocabulary of the LOCAL load-failure kinds `js/admin.js`'s `bail`/
 * `tableroErrorCache` produce when the roster itself could not be built
 * (never sent over the wire — distinct from `admin-liquidacion.service.js`'s
 * own `kind` union, which flows into `tableroCache.error.kind` verbatim on a
 * `preview` failure, AD19). Defined once so both `js/admin.js` and
 * `LiquidacionTableroView.js`'s JSDoc reference the same four literals
 * instead of re-typing them (S2b follow-up a, review-5a2ac077e1b2c3d4).
 */
export const TABLERO_ERROR_KINDS = Object.freeze({
    EMPLEADOS: 'empleados',
    FICHADAS: 'fichadas',
    HISTORIAL: 'historial',
    FUERA_DE_VENTANA: 'fuera_de_ventana',
});

/**
 * Bounds for the tablero's padded `loadFichadas` read (design.md AD3), as
 * real UTC instants of ART local midnight — same window as
 * `api/liquidacion.js`'s fichadas read: from ART midnight `padDias` days
 * before `desde` up to the ART midnight that closes `hasta`'s ART day.
 *
 * Never a bare `'YYYY-MM-DD'`: `listFichadas` compares it against
 * `entrada_at` (`timestamptz`), and the database casts a bare date at UTC
 * midnight = 21:00 ART of the previous day, which hid every shift starting
 * 21:00-23:59 ART on the period's last day (owner requirement: clock in until
 * 23:59 ART, every fichada counts for its day). `listFichadas`' upper bound
 * is inclusive, so an entrada at exactly 00:00 ART after `hasta` is read too;
 * harmless, since every consumer re-filters by the REAL (unpadded) range.
 * @param {{ desde: string, hasta: string }} rango ART calendar dates `YYYY-MM-DD`
 * @param {number} padDias whole days read before `desde`
 * @returns {{ desde: string, hasta: string }} ISO 8601 instants
 */
export function ventanaLecturaFichadas({ desde, hasta }, padDias) {
    return { desde: artMidnightUtcIso(desde, -padDias), hasta: artMidnightUtcIso(hasta, 1) };
}

/**
 * Formatea minutos EXACTOS (posiblemente fraccionarios) como "Hh MMm" — MISMA
 * regla que el `formatMinutos` local de `FichadasView.js` (no exportado de
 * ahí, así que se reimplementa acá en vez de importar de un componente de
 * vista desde una policy pura). Redondeo SOLO de presentación.
 * @param {number} minutos
 * @returns {string}
 */
function formatMinutosComoHoras(minutos) {
    const totalRedondeado = Math.round(minutos);
    const horas = Math.floor(totalRedondeado / 60);
    const mins = totalRedondeado % 60;
    return `${horas}h ${String(mins).padStart(2, '0')}m`;
}

/**
 * Spec "Worked Time Column": hours as "Xh YYm" for `hora`, days as "N días"
 * for `dia` (no singular/plural special-casing — same convention "N
 * anomalías" already uses in this view for any N), "Sueldo fijo" for `fijo`
 * — and for any unset/unrecognized `tipo_pago`, since only `hora`/`dia`
 * employees can ever be "Sin horas" (spec "Fixed-salary employee without
 * shifts stays Listo").
 * @param {'hora'|'dia'|'fijo'|null|undefined} tipoPago
 * @param {{ minutos?: number, dias?: number }} [cantidades]
 * @returns {string}
 */
export function formatTrabajado(tipoPago, { minutos = 0, dias = 0 } = {}) {
    if (tipoPago === 'hora') return formatMinutosComoHoras(minutos);
    if (tipoPago === 'dia') return `${dias} días`;
    return 'Sueldo fijo';
}

/**
 * Suma minutos de fichadas CERRADAS y NO anuladas cuyo bucket (vía
 * `splitMinutesByDate`) cae dentro de `[desde, hasta]` — mismo alcance de
 * rango que `detectAnomalies`/`fechasEnRango`, pero SOLO turnos cerrados: un
 * turno abierto es una anomalía (`ABIERTA`), nunca tiempo trabajado, así que
 * no aporta minutos acá (spec "Worked Time Column", restringido al período
 * SIN padding — el llamador pasa el `desde`/`hasta` real, nunca el padeado).
 * @param {Array<{ entrada_at: string, salida_at?: string|null, anulada?: boolean }>} fichadas
 * @param {string} desde `YYYY-MM-DD`
 * @param {string} hasta `YYYY-MM-DD`
 * @returns {number} minutos exactos, sin redondear
 */
export function minutosTrabajadosEnRango(fichadas, desde, hasta) {
    let total = 0;
    for (const fichada of fichadas ?? []) {
        if (fichada.anulada) continue;
        if (fichada.salida_at == null) continue; // turno abierto: anomalía, no tiempo trabajado
        for (const bucket of splitMinutesByDate(fichada)) {
            if (bucket.fecha < desde || bucket.fecha > hasta) continue;
            total += bucket.minutos;
        }
    }
    return total;
}

/**
 * Cuenta días distintos trabajados por fichadas CERRADAS y NO anuladas,
 * atribuyendo cada turno ÍNTEGRO a la fecha ART de su `entrada_at` — MISMO
 * criterio que `js/shared/payroll-policy.js#diasPorEntrada` (el motor de
 * sueldos ya usa esa atribución para `tipo_pago = 'dia'`; esta cuenta debe
 * coincidir con lo que realmente se liquida, no inventar su propio criterio
 * de "día trabajado"). Un turno abierto o anulado no cuenta.
 * @param {Array<{ entrada_at: string, salida_at?: string|null, anulada?: boolean }>} fichadas
 * @param {string} desde `YYYY-MM-DD`
 * @param {string} hasta `YYYY-MM-DD`
 * @returns {number}
 */
export function diasTrabajadosEnRango(fichadas, desde, hasta) {
    const dias = new Set();
    for (const fichada of fichadas ?? []) {
        if (fichada.anulada) continue;
        if (fichada.salida_at == null) continue;
        const [primerBucket] = splitMinutesByDate(fichada);
        const fechaEntrada = primerBucket.fecha;
        if (fechaEntrada < desde || fechaEntrada > hasta) continue;
        dias.add(fechaEntrada);
    }
    return dias.size;
}

/**
 * Trabajado de una fila "Liquidada" — spec: "from the frozen settlement's
 * detalle", nunca recalculado a partir de fichadas (las cifras ya están
 * congeladas). Campos ausentes (settlements viejos sin este `detalle`, o
 * fixtures de test) degradan a cero en vez de tirar.
 * @param {'hora'|'dia'|'fijo'|null|undefined} tipoPago
 * @param {{ minutos_normales?: number, minutos_feriado?: number, dias_normales?: number, dias_feriado?: number }|null|undefined} detalle
 * @returns {string}
 */
export function trabajadoDeDetalle(tipoPago, detalle) {
    const minutos = (detalle?.minutos_normales ?? 0) + (detalle?.minutos_feriado ?? 0);
    const dias = (detalle?.dias_normales ?? 0) + (detalle?.dias_feriado ?? 0);
    return formatTrabajado(tipoPago, { minutos, dias });
}

/**
 * Anomalías de UN empleado (Unit S3, design.md AD7, spec "Inline Anomalies
 * Drawer") — filtra `fichadas` por `empleado_id` y corre `detectAnomalies`
 * sobre ese subconjunto con el `now`/`desde`/`hasta` REALES (sin padding),
 * MISMO llamado que `splitTableroRoster` ya hace para clasificar la fila "N
 * anomalías" — así el cajón nunca puede mostrar una lista que no coincide
 * con el conteo de la pill. Enriquecido igual que
 * `admin-fichadas.service.js#loadFichadas` (`entrada_at`/`salida_at`/
 * `minutosTotal`) para que la vista no tenga que volver a mirar `fichadas`.
 * @param {Array<{ id: string, empleado_id: string, entrada_at: string, salida_at?: string|null, anulada?: boolean }>} fichadas
 * @param {string} empleadoId
 * @param {{ now: Date, desde: string, hasta: string }} params
 * @returns {Array<{ fichadaId: string, tipo: string, entrada_at: string|null, salida_at: string|null, minutosTotal: number }>}
 */
export function anomaliasDeEmpleado(fichadas, empleadoId, { now, desde, hasta } = {}) {
    const propias = (fichadas ?? []).filter((f) => f.empleado_id === empleadoId);
    const detectadas = detectAnomalies(propias, { now, desde, hasta });
    const byId = new Map(propias.map((f) => [f.id, f]));
    return detectadas.map(({ fichadaId, tipo }) => {
        const fichada = byId.get(fichadaId);
        const minutosTotal = fichada ? splitMinutesByDate(fichada).reduce((total, bucket) => total + bucket.minutos, 0) : 0;
        return {
            fichadaId,
            tipo,
            entrada_at: fichada?.entrada_at ?? null,
            salida_at: fichada?.salida_at ?? null,
            minutosTotal,
        };
    });
}

/**
 * Agrupa filas por `empleado_id` (fichadas, turnos abiertos, liquidaciones).
 * @param {Array<{ empleado_id: string }>|null|undefined} filas
 * @returns {Map<string, Array<object>>}
 */
function agruparPorEmpleado(filas) {
    const porEmpleado = new Map();
    for (const fila of filas ?? []) {
        const arr = porEmpleado.get(fila.empleado_id);
        if (arr) arr.push(fila);
        else porEmpleado.set(fila.empleado_id, [fila]);
    }
    return porEmpleado;
}

/**
 * Clasifica el roster en CUATRO baldes, client-side, sin ningún Fudo I/O:
 * "Liquidada" (overlap con un `confirmada` del historial, AD4 — nunca
 * containment, nunca una fila `anulada`), "N anomalías"
 * (`detectAnomalies` sobre las fichadas del empleado, con el `desde`/`hasta`
 * REALES del período), "Sin horas" (spec "Row Status Presentation": un
 * empleado `hora`/`dia` con tiempo trabajado cero — NUNCA un `fijo`), "Listo"
 * en cualquier otro caso. Sólo los ids "Listo" entran al batch de `preview`;
 * "Sin horas" queda fuera del batch Y de los totales igual que "N anomalías"
 * (ambos con `figuras: null`).
 *
 * `abiertas` (F-F): fichadas abiertas de CUALQUIER antigüedad (la lectura del
 * banner, `listFichadasAbiertas`). La ventana de `fichadas` arranca en
 * `desde` menos el pad, así que un turno abierto más viejo no está ahí; sólo
 * se suma a la regla de turnos sin salida (deduplicado por id), nunca a
 * anomalías ni a tiempo trabajado.
 * @param {{ empleados: Array<object>, fichadas: Array<object>, abiertas?: Array<object>, historial: Array<object>, desde: string, hasta: string, mostrarInactivos: boolean, now: Date }} params
 * @returns {{ filas: Map<string, object>, listoIds: string[] }}
 */
export function splitTableroRoster({ empleados, fichadas, abiertas = [], historial, desde, hasta, mostrarInactivos, now }) {
    const fichadasPorEmpleado = agruparPorEmpleado(fichadas);
    const abiertasPorEmpleado = agruparPorEmpleado(abiertas);

    // AD4: OVERLAP, no containment. Sólo filas `confirmada`; la primera que
    // matchee por empleado gana.
    const liquidadaPorEmpleado = new Map();
    for (const l of historial ?? []) {
        if (l.estado !== 'confirmada') continue;
        if (!(l.periodo_desde <= hasta && l.periodo_hasta >= desde)) continue;
        if (!liquidadaPorEmpleado.has(l.empleado_id)) liquidadaPorEmpleado.set(l.empleado_id, l);
    }

    const roster = (empleados ?? []).filter((e) => {
        if (e.activo) return true;
        if (!mostrarInactivos) return false;
        const tieneFichadas = (fichadasPorEmpleado.get(e.id) ?? []).some((f) =>
            splitMinutesByDate(f).some((b) => b.fecha >= desde && b.fecha <= hasta),
        );
        return tieneFichadas || liquidadaPorEmpleado.has(e.id);
    });

    // L10: liquidaciones del historial por empleado, para `fijoYaLiquidadoEnMes`
    // (el MISMO helper con el que `api/liquidacion.js` rechaza el confirm).
    const historialPorEmpleado = agruparPorEmpleado(historial);

    const filas = new Map();
    const listoIds = [];
    for (const empleado of roster) {
        const liquidacion = liquidadaPorEmpleado.get(empleado.id);
        if (liquidacion) {
            filas.set(empleado.id, {
                id: empleado.id,
                nombre: empleado.nombre,
                activo: empleado.activo,
                bucket: 'liquidada',
                tipoPago: empleado.tipo_pago ?? null,
                anomaliasCount: 0,
                jornadasAbiertas: [],
                liquidacionPeriodo: { desde: liquidacion.periodo_desde, hasta: liquidacion.periodo_hasta },
                trabajado: trabajadoDeDetalle(empleado.tipo_pago, liquidacion.detalle),
                figuras: {
                    bruto: liquidacion.bruto,
                    consumos: liquidacion.consumos,
                    adelantos: liquidacion.adelantos,
                    // Spec liquidacion-otros-descuentos (D5): congelado en
                    // `detalle`; una liquidación anterior a este cambio no
                    // tiene la clave y descontó 0.
                    otros_descuentos: Number(liquidacion.detalle?.otros_descuentos ?? 0),
                    neto: liquidacion.neto,
                },
                // S7 (design.md AD16): la fila matcheada COMPLETA, para que el
                // print en lote pueda armar `construirComprobante(row)` sin
                // una lectura nueva ni re-derivar el predicado de overlap de
                // AD4 una segunda vez en `js/admin.js`.
                liquidacionRow: liquidacion,
            });
            continue;
        }

        const fichadasEmpleado = fichadasPorEmpleado.get(empleado.id) ?? [];
        const anomalias = detectAnomalies(fichadasEmpleado, { now, desde, hasta });
        // F-F: turnos sin salida que tocan el período — la MISMA regla con la
        // que `api/liquidacion.js` rechaza el confirm (409 jornadas_abiertas).
        // No cambia el balde ni saca la fila del preview: `loteLiquidable` la
        // deja fuera del lote y la vista avisa y deshabilita su "Liquidar".
        const idsLeidos = new Set(fichadasEmpleado.map((f) => f.id));
        const candidatasAbiertas = [
            ...fichadasEmpleado,
            ...(abiertasPorEmpleado.get(empleado.id) ?? []).filter((f) => !idsLeidos.has(f.id)),
        ];
        const jornadasAbiertas = jornadasAbiertasEnPeriodo(candidatasAbiertas, { hasta }).map((f) => ({
            fichadaId: f.id,
            entrada_at: f.entrada_at,
        }));
        // L10/D1: un fijo que ya tiene una liquidación confirmada de sueldo
        // fijo que ARRANCA en el mismo mes que este período no se vuelve a
        // liquidar (el servidor rechaza con 409 fijo_ya_liquidado_mes). La fila conserva su balde y sus cifras;
        // `loteLiquidable` la deja fuera del lote y la vista avisa y
        // deshabilita su "Liquidar".
        const fijoYaLiquidado = fijoYaLiquidadoEnMes({
            tipoPago: empleado.tipo_pago,
            desde,
            hasta,
            liquidaciones: historialPorEmpleado.get(empleado.id) ?? [],
        });

        if (anomalias.length > 0) {
            filas.set(empleado.id, {
                id: empleado.id,
                nombre: empleado.nombre,
                activo: empleado.activo,
                bucket: 'anomalias',
                tipoPago: empleado.tipo_pago ?? null,
                jornadasAbiertas,
                fijoYaLiquidado,
                anomaliasCount: anomalias.length,
                liquidacionPeriodo: null,
                trabajado: computeTrabajadoTexto(empleado.tipo_pago, fichadasEmpleado, desde, hasta),
                figuras: null,
            });
            continue;
        }

        const { texto: trabajado, sinHoras } = computeTrabajado(empleado.tipo_pago, fichadasEmpleado, desde, hasta);
        if ((empleado.tipo_pago === 'hora' || empleado.tipo_pago === 'dia') && sinHoras) {
            filas.set(empleado.id, {
                id: empleado.id,
                nombre: empleado.nombre,
                activo: empleado.activo,
                bucket: 'sin_horas',
                tipoPago: empleado.tipo_pago ?? null,
                jornadasAbiertas,
                fijoYaLiquidado,
                anomaliasCount: 0,
                liquidacionPeriodo: null,
                trabajado,
                figuras: null,
            });
            continue;
        }

        filas.set(empleado.id, {
            id: empleado.id,
            nombre: empleado.nombre,
            activo: empleado.activo,
            bucket: 'listo',
            tipoPago: empleado.tipo_pago ?? null,
            jornadasAbiertas,
            fijoYaLiquidado,
            anomaliasCount: 0,
            liquidacionPeriodo: null,
            trabajado,
            figuras: null,
        });
        listoIds.push(empleado.id);
    }

    return { filas, listoIds };
}

/**
 * U4 — total of the ACTIVE (non-voided) advances per employee, summed in
 * integer cents (never float drift on money). Used to warn on a "Sin horas"
 * row: an hourly/daily employee with no worked time cannot be settled, so
 * the advances dated in that period would never be deducted anywhere.
 * Employees whose advances are all voided get no entry. The same sum serves
 * the otros descuentos ledger (spec liquidacion-otros-descuentos, B5): its
 * rows have the same `empleado_id`/`monto`/`anulado_at` shape.
 * @param {Array<{ empleado_id: string, monto: number|string, anulado_at: string|null }>|null|undefined} adelantos
 * @returns {Map<string, number>} empleado_id -> total in pesos
 */
export function adelantosActivosPorEmpleado(adelantos) {
    const centavosPorEmpleado = new Map();
    for (const a of Array.isArray(adelantos) ? adelantos : []) {
        if (a.anulado_at) continue;
        const centavos = Math.round(Number(a.monto) * 100);
        if (!Number.isFinite(centavos) || centavos <= 0) continue;
        centavosPorEmpleado.set(a.empleado_id, (centavosPorEmpleado.get(a.empleado_id) ?? 0) + centavos);
    }
    const totales = new Map();
    for (const [empleadoId, centavos] of centavosPorEmpleado) totales.set(empleadoId, centavos / 100);
    return totales;
}

/**
 * U3 — the ids "Liquidar N listos" settles: every "Listo" row, EXCEPT the
 * `tipo_pago = 'fijo'` ones when `desde`..`hasta` is not exactly one
 * calendar month (`esMesCompleto`). A fijo employee is settled its full
 * monthly `valor` in every settlement (`payroll-policy.js`), so a weekly or
 * quincena batch would pay the whole salary each time. Those rows stay
 * "Listo" (their own "Liquidar" still works, with a warning in the confirm
 * dialog); they are only kept out of the bulk batch, and returned in
 * `fijosExcluidos` so the view can say why.
 *
 * F-F: a "Listo" row with an open shift overlapping the period
 * (`fila.jornadasAbiertas`) is left out too — the server would refuse the
 * whole batch's confirm with 409 `jornadas_abiertas` — and returned in
 * `jornadasAbiertasExcluidas`.
 *
 * L10/D1: a fijo "Listo" row that already has a confirmed fixed-salary
 * settlement starting in the range's start month (`fila.fijoYaLiquidado`,
 * `fijoYaLiquidadoEnMes`) is left out too — the server
 * would refuse the whole batch with 409 `fijo_ya_liquidado_mes` — and
 * returned in `fijosYaLiquidadosExcluidos`.
 * @param {Iterable<object>} filas tablero rows (`splitTableroRoster`)
 * @param {{ desde: string, hasta: string }|null} rango
 * @returns {{ ids: string[], fijosExcluidos: Array<object>, jornadasAbiertasExcluidas: Array<object>, fijosYaLiquidadosExcluidos: Array<object> }}
 */
export function loteLiquidable(filas, rango) {
    const mesCompleto = Boolean(rango) && esMesCompleto(rango.desde, rango.hasta);
    const ids = [];
    const fijosExcluidos = [];
    const jornadasAbiertasExcluidas = [];
    const fijosYaLiquidadosExcluidos = [];
    for (const fila of filas ?? []) {
        if (fila.bucket !== 'listo') continue;
        if (fila.jornadasAbiertas?.length > 0) {
            jornadasAbiertasExcluidas.push(fila);
            continue;
        }
        if (fila.fijoYaLiquidado) {
            fijosYaLiquidadosExcluidos.push(fila);
            continue;
        }
        if (fila.tipoPago === 'fijo' && !mesCompleto) {
            fijosExcluidos.push(fila);
            continue;
        }
        ids.push(fila.id);
    }
    return { ids, fijosExcluidos, jornadasAbiertasExcluidas, fijosYaLiquidadosExcluidos };
}

/**
 * @param {'hora'|'dia'|'fijo'|null|undefined} tipoPago
 * @param {Array<object>} fichadasEmpleado
 * @param {string} desde
 * @param {string} hasta
 * @returns {{ texto: string, sinHoras: boolean }}
 */
function computeTrabajado(tipoPago, fichadasEmpleado, desde, hasta) {
    if (tipoPago === 'hora') {
        const minutos = minutosTrabajadosEnRango(fichadasEmpleado, desde, hasta);
        return { texto: formatTrabajado('hora', { minutos }), sinHoras: minutos === 0 };
    }
    if (tipoPago === 'dia') {
        const dias = diasTrabajadosEnRango(fichadasEmpleado, desde, hasta);
        return { texto: formatTrabajado('dia', { dias }), sinHoras: dias === 0 };
    }
    return { texto: 'Sueldo fijo', sinHoras: false };
}

/** Same as {@link computeTrabajado} but only the text — used by the "N anomalías" bucket, which never evaluates "Sin horas". */
function computeTrabajadoTexto(tipoPago, fichadasEmpleado, desde, hasta) {
    return computeTrabajado(tipoPago, fichadasEmpleado, desde, hasta).texto;
}

/**
 * Vuelca el resultado de `preview` sobre las filas "Listo" (mutación
 * in-place del `Map` recién armado por `splitTableroRoster` — todavía no
 * publicado en `tableroCache`, así que esto es seguro). Una fila cuyo
 * `empleado_id` no aparece en la respuesta (preview falló, o ese empleado no
 * se pidió) queda con `figuras: null` — la vista ya sabe pintar eso como
 * "—" (AD19).
 * @param {Map<string, object>} filas
 * @param {object} previewResult envelope de `previewLiquidacion`
 */
export function applyPreviewToFilas(filas, previewResult) {
    if (!previewResult?.ok) return;
    for (const r of previewResult.liquidaciones ?? []) {
        const fila = filas.get(r.empleado_id);
        if (!fila || fila.bucket !== 'listo') continue;
        fila.figuras = { bruto: r.bruto, consumos: r.consumos, adelantos: r.adelantos, otros_descuentos: r.otros_descuentos, neto: r.neto };
    }
}

/**
 * Suma bruto/consumos/adelantos/otros_descuentos/neto sobre las filas CON figuras (Listo +
 * Liquidada) — spec "Totals Row": una fila sin figuras ("N anomalías" o
 * "Sin horas") no contribuye nada.
 * @param {Map<string, object>|null} filas
 * @returns {{bruto:number,consumos:number,adelantos:number,otros_descuentos:number,neto:number}|null}
 */
export function computeTableroTotales(filas) {
    if (!filas) return null;
    const totales = { bruto: 0, consumos: 0, adelantos: 0, otros_descuentos: 0, neto: 0 };
    for (const fila of filas.values()) {
        if (!fila.figuras) continue;
        totales.bruto += Number(fila.figuras.bruto) || 0;
        totales.consumos += Number(fila.figuras.consumos) || 0;
        totales.adelantos += Number(fila.figuras.adelantos) || 0;
        totales.otros_descuentos += Number(fila.figuras.otros_descuentos) || 0;
        totales.neto += Number(fila.figuras.neto) || 0;
    }
    return totales;
}
